import { Box, Tooltip as MuiTooltip, Typography } from '@mui/material'
import { useTranslate } from 'ra-core'
import { FunctionField, useRecordContext } from 'react-admin'

import {
  THEME_LINEAR_GRADIENT_GREEN,
  THEME_LINEAR_GRADIENT_ORANGE,
  THEME_LINEAR_GRADIENT_RED,
  THEME_LINEAR_GRADIENT_YELLOW,
  useCommonStyles,
  useLinearGradientCommonStyles,
} from '../config/theme'

const VERSION_DIFFERENCE_MAJOR = 'major'
const VERSION_DIFFERENCE_MINOR = 'minor'
const VERSION_DIFFERENCE_PATCH = 'patch'
const VERSION_DIFFERENCE_NONE = 'none'

const VERSION_DIFFERENCE_COLORS = {
  [VERSION_DIFFERENCE_MAJOR]: THEME_LINEAR_GRADIENT_RED,
  [VERSION_DIFFERENCE_MINOR]: THEME_LINEAR_GRADIENT_ORANGE,
  [VERSION_DIFFERENCE_PATCH]: THEME_LINEAR_GRADIENT_YELLOW,
  [VERSION_DIFFERENCE_NONE]: THEME_LINEAR_GRADIENT_GREEN,
}

const parseSemVer = (version) => {
  const parts = version.split('.')
  const [major, minor, patch] = parts.map((n) => parseInt(n, 10))
  return { major, minor, patch }
}

const getAppVersionDifferenceLevelFromLatestVersion = (appVersion, latestVersion) => {
  const { major: appMajor, minor: appMinor, patch: appPatch } = parseSemVer(appVersion)
  const { major: latestMajor, minor: latestMinor, patch: latestPatch } = parseSemVer(latestVersion)

  if (latestMajor > appMajor) {
    return VERSION_DIFFERENCE_MAJOR
  } else if (latestMajor < appMajor) {
    return VERSION_DIFFERENCE_NONE
  }
  if (latestMinor > appMinor) {
    return VERSION_DIFFERENCE_MINOR
  } else if (latestMinor < appMinor) {
    return VERSION_DIFFERENCE_NONE
  }
  if (latestPatch > appPatch) {
    return VERSION_DIFFERENCE_PATCH
  }
  return VERSION_DIFFERENCE_NONE
}

const NotUpToDateTooltip = ({ latestVersion }) => {
  const translate = useTranslate()
  return (
    <div style={{ padding: 4 }}>
      <div style={{ marginBottom: 2 }}>{translate('resources.users.show.mobileAppNotUpToDate')}</div>
      <div>
        {translate('resources.users.show.latestVersion')}: v{latestVersion}
      </div>
    </div>
  )
}

const MobileAppVersionField = ({ mobileAppLatestVersion, ...props }) => {
  const { [props.source]: userAgent } = useRecordContext()
  const displayedVersion = userAgent?.replace(/^(com|be)\.(.*)\sv/, 'v')
  const shortVersion = userAgent?.replace(/^(com|be)\.(.*)\sv/, '').replace(/\s(.*)/, '')
  const versionDifferenceLevel = shortVersion
    ? getAppVersionDifferenceLevelFromLatestVersion(shortVersion, mobileAppLatestVersion)
    : null
  const versionDifferenceColor = VERSION_DIFFERENCE_COLORS[versionDifferenceLevel]

  const translate = useTranslate()
  const commonClasses = useCommonStyles()
  const linearGradientCommonClasses = useLinearGradientCommonStyles()

  const tooltip =
    versionDifferenceLevel === VERSION_DIFFERENCE_NONE ? (
      translate('resources.users.show.mobileAppUpToDate')
    ) : (
      <NotUpToDateTooltip latestVersion={mobileAppLatestVersion} />
    )

  return (
    <FunctionField
      {...props}
      render={() =>
        userAgent ? (
          <div style={commonClasses.flexRowCenterContainer}>
            <MuiTooltip title={tooltip} placement="left">
              <Box sx={{ ...commonClasses.smallDot, ...linearGradientCommonClasses[versionDifferenceColor] }} />
            </MuiTooltip>
            <Typography variant="body2">{displayedVersion}</Typography>
          </div>
        ) : (
          'n/a'
        )
      }
    />
  )
}

MobileAppVersionField.defaultProps = {
  source: 'user_agent',
  mobileAppLatestVersion: '0.0.0',
}

export default MobileAppVersionField
